import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { FieldType, IAddress, ILocation, IField } from '@baishuyun/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 复制文本的核心函数（TypeScript 版本）
export async function copyTextToClipboard(text: string): Promise<boolean> {
  // 校验输入：确保复制的文本是字符串且非空
  if (typeof text !== 'string' || text.trim() === '') {
    console.error('要复制的文本必须是非空字符串');
    return false;
  }

  try {
    // 方案1：优先使用现代浏览器的 Clipboard API（更安全、无DOM操作）
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      console.log('文本复制成功（Clipboard API）');
      return true;
    }

    // 方案2：降级使用 execCommand（兼容旧浏览器，需创建临时DOM元素）
    // 1. 创建临时textarea元素（隐藏，避免影响页面布局）
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // 避免滚动条偏移
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);

    // 2. 选中文本并执行复制
    textarea.focus();
    textarea.select();
    const success = document.execCommand('copy');

    // 3. 移除临时元素
    document.body.removeChild(textarea);

    if (success) {
      console.log('文本复制成功（execCommand 降级方案）');
      return true;
    } else {
      throw new Error('execCommand 复制失败');
    }
  } catch (error) {
    // 捕获复制失败的异常（如用户拒绝权限、浏览器不支持等）
    console.error('文本复制失败：', error);
    return false;
  }
}

function isOptionArray(value: unknown): value is Array<{ label: string; value: string }> {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      'label' in item &&
      'value' in item &&
      typeof (item as any).label === 'string' &&
      typeof (item as any).value === 'string'
  );
}

export const toHumanReadable = (value: unknown, type: FieldType): string | unknown => {
  if (type === 'address' && typeof value === 'object') {
    const v = value as IAddress;
    return `${v.province} ${v.city} ${v.district} ${v.detail}`;
  }

  if (type === 'location' && typeof value === 'object') {
    const v = value as ILocation;
    const lnglatXY = v.lnglatXY || ['', ''];
    return `经度: ${lnglatXY[0] || ''}, 纬度: ${lnglatXY[1] || ''}`;
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (isOptionArray(value)) {
    const values = value.map((item) => item.value);
    return values.join(', ');
  }

  return value;
};

export const isSubformFieldEqual = (f1: IField, f2: IField, widgetArrKey: string = 'value') => {
  // 不是子表单字段，不进行比较，视为相等
  if (f1?.widget?.type !== 'subform' || f2?.widget?.type !== 'subform') {
    return true;
  }

  const subFields1 = (f1?.widget as any)[widgetArrKey] || [];
  const subFields2 = (f2?.widget as any)[widgetArrKey] || [];

  // 子表单字段长度不等，视为不同
  if (subFields1.length !== subFields2.length) {
    return false;
  }

  for (let i = 0; i < subFields1.length; i++) {
    const subField1 = subFields1[i];
    const subField2 = subFields2[i];

    if (subField1.length !== subField2.length) {
      return false;
    }
  }

  return true;
};
